import React, { useState, useEffect } from "react";
import CommonSelect from "../../../../../core/common/commonSelect";

import { ReportTypeFilterProps } from "../types";

const ReportTypeFilter: React.FC<ReportTypeFilterProps> = ({
  data,
  setFilteredData,
}) => {
  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "All" | "Active" | "Inactive"
  >("All");
  const [frequencyFilter, setFrequencyFilter] = useState<string>("All");
  const [sortBy, setSortBy] = useState<"name" | "created" | "frequency">(
    "name"
  );

  // Filter Options
  const statusOptions = [
    { value: "All", label: "All Status" },
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];

  const frequencyOptions = [
    { value: "All", label: "All Frequencies" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "never", label: "Never" },
  ];

  const sortOptions = [
    { value: "name", label: "Sort by Name" },
    { value: "created", label: "Recently Created" },
    { value: "frequency", label: "Frequency Type" },
  ];

  // Active filters count
  const activeFiltersCount = [
    searchTerm,
    statusFilter !== "All" ? statusFilter : null,
    frequencyFilter !== "All" ? frequencyFilter : null,
  ].filter(Boolean).length;

  // Helper functions
  const getStatusText = (status: 0 | 1): "Active" | "Inactive" => {
    return status === 1 ? "Active" : "Inactive";
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("All");
    setFrequencyFilter("All");
    setSortBy("name");
  };

  // Filter and sort logic
  useEffect(() => {
    let filteredData = [...data];

    // Search filter
    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase().trim();
      filteredData = filteredData.filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query) ||
          item.company_type.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== "All") {
      filteredData = filteredData.filter(
        (item) => getStatusText(item.status) === statusFilter
      );
    }

    // Frequency filter
    if (frequencyFilter !== "All") {
      filteredData = filteredData.filter(
        (item) => item.frequency_type === frequencyFilter
      );
    }

    // Sort data
    filteredData.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "created":
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        case "frequency":
          return a.frequency_type.localeCompare(b.frequency_type);
        default:
          return 0;
      }
    });

    setFilteredData(filteredData);
  }, [
    data,
    searchTerm,
    statusFilter,
    frequencyFilter,
    sortBy,
    setFilteredData,
  ]);

  return (
    <div
      className="card modern-filter-card border-0 mb-3 shadow-sm"
      style={{ zIndex: 10 }}>
      <div className="card-body bg-white">
        <div className="row align-items-center justify-content-end g-3">
          <div className="col-lg-2 col-md-2">
            <h4>Report Type Lists</h4>
          </div>

          {/* Search Input */}
          <div className="col-lg-2 col-md-3">
            <div className="input-icon-end position-relative">
              <input
                type="text"
                className="form-control form-control-modern"
                placeholder="Search report types..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="input-icon-addon">
                <i className="ti ti-search small"></i>
              </span>
            </div>
          </div>

          {/* Status Filter */}
          <div className="col-lg-2 col-md-3">
            <CommonSelect
              className="form small"
              options={statusOptions}
              defaultValue={statusOptions.find(
                (option) => option.value === statusFilter
              )}
              onChange={(selected: any) =>
                setStatusFilter(selected?.value || "All")
              }
              placeHolder="Status"
            />
          </div>

          {/* Frequency Filter */}
          <div className="col-lg-2 col-md-3">
            <CommonSelect
              className="form small"
              options={frequencyOptions}
              defaultValue={frequencyOptions.find(
                (option) => option.value === frequencyFilter
              )}
              onChange={(selected: any) =>
                setFrequencyFilter(selected?.value || "All")
              }
              placeHolder="Frequency"
            />
          </div>

          {/* Sort By */}
          <div className="col-lg-2 col-md-3">
            <CommonSelect
              className="form small"
              options={sortOptions}
              defaultValue={sortOptions.find(
                (option) => option.value === sortBy
              )}
              onChange={(selected: any) => setSortBy(selected?.value || "name")}
              placeHolder="Sort By"
            />
          </div>

          <div className="w-auto d-flex align-items-center">
            <button
              className="btn btn-outline-danger btn-clear-filters"
              onClick={handleClearFilters}
              disabled={activeFiltersCount === 0}>
              <i className="ti ti-filter-off me-1"></i>
              Clear All
              <span className="badge bg-danger text-white ms-2 rounded-pill">
                {activeFiltersCount}
              </span>
            </button>
          </div>

          {/* Results Count & Clear Filters */}
        </div>

        {/* Second Row - Advanced Filters & Clear */}
        {activeFiltersCount > 0 && (
          <div className="row align-items-center justify-content-start g-3 mt-0">
            <div className="col-lg-11 col-md-11">
              {/* Filter Tags */}
              <div className="filter-tags d-flex flex-wrap gap-3 justify-content-end">
                {searchTerm && (
                  <span className="badge badge-secondary-transparent text-primary badge-md px-2 py-2 align-items-center">
                    Search: "{searchTerm}"
                    <button
                      type="button"
                      className="btn text-danger px-0 py-0 ms-1"
                      onClick={() => setSearchTerm("")}>
                      <i className="ti ti-x" />
                    </button>
                  </span>
                )}
                {statusFilter !== "All" && (
                  <span className="badge badge-success-transparent badge-md px-2 py-2 align-items-center">
                    Status: {statusFilter}
                    <button
                      type="button"
                      className="btn px-0 py-0 ms-1"
                      onClick={() => setStatusFilter("All")}>
                      <i className="ti ti-x" />
                    </button>
                  </span>
                )}
                {frequencyFilter !== "All" && (
                  <span className="badge bg-info-subtle text-info badge-md px-2 py-2 align-items-center">
                    Frequency: {frequencyFilter}
                    <button
                      type="button"
                      className="btn text-danger px-0 py-0 ms-1"
                      onClick={() => setFrequencyFilter("All")}>
                      <i className="ti ti-x" />
                    </button>
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportTypeFilter;
